import {
  Component,
  ElementRef,
  Input,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Chart, ChartConfiguration, Plugin, registerables } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MeasurementListModel } from '../../../../core/models/survey/measurement-list.model';
import { DiabetesCaseInformation } from '../../../../core/models/patient/diabet-case-information.model';
import { LifestyleProfile } from '../../../../core/models/patient/lifestyle-profile.model';
import { QRCodeDetailsModel } from '../../../../core/models/patient/qr-code-details.model';
import { SurveyService } from '../../../../core/services/survey/survey.service';
import { ToastService } from '../../../../core/services/toast.service';
import { ConsultationsService } from '../../../../core/services/consultation/consultations.service';
import { PatientsService } from '../../../../core/services/patient/patients.service';
import { CaseStatus } from '../../../../../enums/case-status.enum';
import { InsulinAdministrationMethod } from '../../../../../enums/insulin-administration-method.enum';
import { CaseActivityLog } from '../../../../core/models/patient/case-activity-log.model';
import { forkJoin } from 'rxjs';
import { MealLog } from '../../../../core/models/survey/meal-log.model';

type MeasurementKind = 'fasting' | 'postprandial' | 'unknown';

interface GlucoseChartPoint {
  measuredDay: string;
  measuredTime: string;
  x: number;
  fasting: number | null;
  postprandial: number | null;
}

interface GlucoseDatasetPoint {
  x: number;
  y: number;
  measuredDay: string;
  measuredTime: string;
}

interface GlucoseSummary {
  average: number | null;
  inRangePercentage: number;
  highPercentage: number;
  lowPercentage: number;
  totalPoints: number;
}

interface ActivityTimelineSection {
  key: string;
  labelKey: string;
  icon: string;
  entries: string[];
}

interface MealTimelineSection {
  key: string;
  labelKey: string;
  icon: string;
  entries: string[];
}

interface MedicationOption {
  id: string;
  name: string;
}

interface InsulinTypeOption {
  id: string;
  name: string;
}

const targetBandPlugin: Plugin<'line'> = {
  id: 'targetBand',
  beforeDatasetsDraw(chart) {
    const pluginOptions = (chart.options.plugins as any)?.targetBand;
    if (!pluginOptions?.display) {
      return;
    }

    const yScale = chart.scales['y'];
    const xScale = chart.scales['x'];

    if (!yScale || !xScale) {
      return;
    }

    const lowerPixel = yScale.getPixelForValue(pluginOptions.lower);
    const upperPixel = yScale.getPixelForValue(pluginOptions.upper);
    const { ctx, chartArea } = chart;

    ctx.save();
    ctx.fillStyle = pluginOptions.backgroundColor ?? 'rgba(217, 236, 208, 0.45)';
    ctx.fillRect(
      chartArea.left,
      upperPixel,
      chartArea.right - chartArea.left,
      lowerPixel - upperPixel
    );
    ctx.restore();
  },
};

@Component({
  selector: 'app-case-information',
  standalone: true,
  imports: [
    TranslateModule,
    FormsModule,
    DatePipe,
    CommonModule,
    BaseChartDirective,
  ],
  templateUrl: './case-information.component.html',
  styleUrl: './case-information.component.scss',
})
export class CaseInformationComponent {
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild(BaseChartDirective) chart!: BaseChartDirective;
  @Input() patientHeaderInformations: any = null;
  @Input() diabetesCaseInformation: DiabetesCaseInformation = null;
  @Input() lifestyleProfile: LifestyleProfile | null = null;
  @Input() consultationId: string = '';

  caseStatus = CaseStatus;
  measurements: MeasurementListModel[] = [];
  chartData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [],
  };
  chartOptions: any = {
    responsive: true,
  };
  isDragOver = false;
  uploadedFile: string | ArrayBuffer | null = null;
  isUploading = false;
  uploadFileName = '';
  times: { id: string; text: string; value: number }[] = [
    { id: 'morning', text: 'Sabah', value: 1 },
    { id: 'noon', text: 'Ogle', value: 2 },
    { id: 'evening', text: 'Aksam', value: 3 },
  ];
  insulinTableHeaders: { id: string; text: string }[] = [
    { id: 'time', text: 'Zaman' },
    { id: 'insulinType', text: 'Insulin Tipi' },
    { id: 'doseIU', text: 'Doz (IU)' },
    { id: 'time', text: 'Zaman' },
  ];
  qrCodeDetails: QRCodeDetailsModel = { url: '' };
  chartSummary: GlucoseSummary = {
    average: null,
    inRangePercentage: 0,
    highPercentage: 0,
    lowPercentage: 0,
    totalPoints: 0,
  };
  activityLogs: Record<number, CaseActivityLog[]> = {
    4: [],
    5: [],
    6: [],
    7: [],
  };
  mealLogs: MealLog[] = [];
  medicationOptions: MedicationOption[] = [];
  insulinTypeOptions: InsulinTypeOption[] = [];

  readonly fastingColor = '#2f7de1';
  readonly postprandialColor = '#ea5b2b';
  readonly bandColor = 'rgba(220, 235, 212, 0.55)';
  selectedWeekStart: Date | null = null;

  constructor(
    private surveyService: SurveyService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private consultationsService: ConsultationsService,
    private patientsService: PatientsService,
    private datePipe: DatePipe
  ) {
    Chart.register(...registerables, targetBandPlugin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['consultationId']?.currentValue) {
      this.getMeasurement(this.consultationId);
      this.getActivityLogs(this.consultationId);
    }
  }

  getMeasurement(id: string): void {
    this.surveyService.measurement(id).subscribe({
      next: (res) => {
        this.measurements = Array.isArray(res) ? res : [];
        this.initializeSelectedWeek(this.measurements);
        this.buildGlucoseReport(this.measurements);
      },
    });
  }

  ngOnInit(): void {
    this.getMedicalInformationsList();
    console.log(
      'CaseInformationComponent initialized',
      this.patientHeaderInformations
    );
  }

  get sortedOtherMedicationSchedules() {
    return [...(this.diabetesCaseInformation?.otherMedicationSchedules ?? [])]
      .sort(
        (left, right) =>
          this.getTimePeriodOrder(left?.timePeriod) -
          this.getTimePeriodOrder(right?.timePeriod)
      )
      .filter((schedule) => this.hasMeaningfulOtherMedicationSchedule(schedule));
  }

  get sortedInsulinMedicationSchedules() {
    return [...(this.diabetesCaseInformation?.insulinMedicationSchedules ?? [])]
      .sort(
        (left, right) =>
          this.getTimePeriodOrder(left?.timePeriod) -
          this.getTimePeriodOrder(right?.timePeriod)
      )
      .filter((schedule) => this.hasMeaningfulInsulinSchedule(schedule));
  }

  get sortedOralMedicationSchedules() {
    return [...(this.diabetesCaseInformation?.oralMedicationSchedules ?? [])]
      .sort(
        (left, right) =>
          this.getTimePeriodOrder(left?.timePeriod) -
          this.getTimePeriodOrder(right?.timePeriod)
      )
      .filter((schedule) => this.hasMeaningfulOralMedicationSchedule(schedule));
  }

  get lifestyleCards(): Array<{ labelKey: string; value: string }> {
    return [
      { labelKey: 'sleepTime', value: this.formatTime(this.lifestyleProfile?.sleepTime) },
      { labelKey: 'wakeUpTime', value: this.formatTime(this.lifestyleProfile?.wakeUpTime) },
      { labelKey: 'breakfastTime', value: this.formatTime(this.lifestyleProfile?.breakfastTime) },
      { labelKey: 'lunchTime', value: this.formatTime(this.lifestyleProfile?.lunchTime) },
      { labelKey: 'dinnerTime', value: this.formatTime(this.lifestyleProfile?.dinnerTime) },
    ];
  }

  get mealTimelineSections(): MealTimelineSection[] {
    return [
      {
        key: 'meal',
        labelKey: 'meal',
        icon: 'fa-solid fa-utensils',
        entries: this.mapMealEntries(),
      },
      {
        key: 'water',
        labelKey: 'water',
        icon: 'fa-solid fa-glass-water',
        entries: this.mapWaterEntries(),
      },
      {
        key: 'alcohol',
        labelKey: 'alcohol',
        icon: 'fa-solid fa-wine-glass',
        entries: this.mapAlcoholEntries(),
      },
    ];
  }

  get activityTimelineSections(): ActivityTimelineSection[] {
    return [
      {
        key: 'sport',
        labelKey: 'sport',
        icon: 'fa-solid fa-dumbbell',
        entries: this.mapActivityEntries(4),
      },
      {
        key: 'toilet',
        labelKey: 'toilet',
        icon: 'fa-solid fa-toilet',
        entries: this.mapActivityEntries(5),
      },
      {
        key: 'stress',
        labelKey: 'stress',
        icon: 'fa-solid fa-face-meh',
        entries: this.mapActivityEntries(7),
      },
      {
        key: 'sleep',
        labelKey: 'sleep',
        icon: 'fa-solid fa-bed',
        entries: this.mapActivityEntries(6),
      },
    ];
  }

  formatTime(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    return value.slice(0, 5);
  }

  getActivityLogs(patientRecordCaseId: string): void {
    forkJoin({
      sport: this.patientsService.getCaseActivityLogsByType(patientRecordCaseId, 4),
      toilet: this.patientsService.getCaseActivityLogsByType(patientRecordCaseId, 5),
      sleep: this.patientsService.getCaseActivityLogsByType(patientRecordCaseId, 6),
      stress: this.patientsService.getCaseActivityLogsByType(patientRecordCaseId, 7),
      meals: this.surveyService.mealLogsByCaseId(patientRecordCaseId),
    }).subscribe({
      next: ({ sport, toilet, sleep, stress, meals }) => {
        this.activityLogs = {
          4: this.sortActivityLogs(sport),
          5: this.sortActivityLogs(toilet),
          6: this.sortActivityLogs(sleep),
          7: this.sortActivityLogs(stress),
        };
        this.mealLogs = this.sortMealLogs(meals);
      },
      error: () => {
        this.activityLogs = { 4: [], 5: [], 6: [], 7: [] };
        this.mealLogs = [];
      },
    });
  }

  private sortActivityLogs(logs: CaseActivityLog[] | null | undefined): CaseActivityLog[] {
    return [...(logs ?? [])].sort(
      (left, right) =>
        new Date(right.activityTime).getTime() -
        new Date(left.activityTime).getTime()
    );
  }

  private mapActivityEntries(actionType: number): string[] {
    return (this.activityLogs[actionType] ?? []).map(
      (log) => this.datePipe.transform(log.activityTime, 'dd.MM.yyyy HH:mm') ?? '-'
    );
  }

  private sortMealLogs(logs: MealLog[] | null | undefined): MealLog[] {
    return [...(logs ?? [])].sort(
      (left, right) =>
        new Date(right.mealDateTime).getTime() -
        new Date(left.mealDateTime).getTime()
    );
  }

  private mapMealEntries(): string[] {
    return this.mealLogs.map((log) => {
      const timeLabel =
        this.datePipe.transform(log.mealDateTime, 'dd.MM.yyyy HH:mm') ?? '-';
      const mealContent = log.mealContent?.trim()
        ? log.mealContent.trim()
        : this.translateService.instant('mealLogFallback');
      return `${timeLabel} - ${mealContent}`;
    });
  }

  private mapWaterEntries(): string[] {
    return this.mealLogs
      .filter((log) => log.hadWater)
      .map((log) => {
        const timeLabel =
          this.datePipe.transform(log.mealDateTime, 'dd.MM.yyyy HH:mm') ?? '-';
        const amount = log.waterAmount?.trim() ? ` (${log.waterAmount.trim()})` : '';
        return `${timeLabel}${amount}`;
      });
  }

  private mapAlcoholEntries(): string[] {
    return this.mealLogs
      .filter((log) => log.hadAlcohol)
      .map((log) => {
        const timeLabel =
          this.datePipe.transform(log.mealDateTime, 'dd.MM.yyyy HH:mm') ?? '-';
        const details = log.alcoholDetails?.trim()
          ? ` - ${log.alcoholDetails.trim()}`
          : '';
        return `${timeLabel}${details}`;
      });
  }

  getMedicalInformationsList(): void {
    this.consultationsService
      .getDiabetesPreparationPageInformations()
      .subscribe({
        next: (res) => {
          this.medicationOptions = Array.isArray(res?.medications)
            ? res.medications
            : [];
          this.insulinTypeOptions = Array.isArray(res?.insulinTypes)
            ? res.insulinTypes
            : [];
        },
        error: () => {
          this.medicationOptions = [];
          this.insulinTypeOptions = [];
        },
      });
  }

  resolveOtherMedicationName(schedule: any): string {
    const usedMedication = schedule?.usedMedications?.[0];
    const freeTextName = usedMedication?.otherMedication?.trim();
    if (freeTextName) {
      return freeTextName;
    }

    const explicitName =
      usedMedication?.medicationName?.trim?.() ??
      usedMedication?.name?.trim?.() ??
      schedule?.medicationName?.trim?.() ??
      schedule?.medicineName?.trim?.();
    if (explicitName) {
      return explicitName;
    }

    const medicationId = usedMedication?.medicationId;
    if (!medicationId) {
      return '-';
    }

    return (
      this.medicationOptions.find((option) => option.id === medicationId)?.name ??
      (this.looksLikeUuid(medicationId) ? '-' : medicationId)
    );
  }

  resolveInsulinTypeNames(schedule: any): string[] {
    const rawTypes: any[] = Array.isArray(schedule?.insulinTypes)
      ? schedule.insulinTypes
      : [];

    return rawTypes
      .filter((type) => this.isSelectedInsulinType(type))
      .map((type) => this.resolveInsulinTypeName(type))
      .filter((name, index, list) => !!name && list.indexOf(name) === index);
  }

  private getTimePeriodOrder(timePeriod: number | null | undefined): number {
    switch (timePeriod) {
      case 1:
        return 1;
      case 2:
        return 2;
      case 3:
        return 3;
      default:
        return 99;
    }
  }

  private looksLikeUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value
    );
  }

  private hasMeaningfulInsulinSchedule(schedule: any): boolean {
    if (schedule?.selected === false) {
      return false;
    }

    const insulinTypeNames = this.resolveInsulinTypeNames(schedule);
    const dosage = Number(schedule?.dosage ?? 0);
    const time = this.normalizeDisplayTime(schedule?.time);

    return insulinTypeNames.length > 0 || dosage > 0 || time !== '-';
  }

  private hasMeaningfulOralMedicationSchedule(schedule: any): boolean {
    if (schedule?.selected === false) {
      return false;
    }

    const medicationName = this.normalizeDisplayText(
      schedule?.medicationName ?? schedule?.medicineName ?? schedule?.medicationId
    );
    const dosage = Number(schedule?.dosage ?? 0);
    const time = this.normalizeDisplayTime(schedule?.time);

    return medicationName !== '-' || dosage > 0 || time !== '-';
  }

  private hasMeaningfulOtherMedicationSchedule(schedule: any): boolean {
    const medicationName = this.resolveOtherMedicationName(schedule);
    const dosage = Number(schedule?.dosage ?? 0);
    const time = this.normalizeDisplayTime(schedule?.time);

    return medicationName !== '-' || dosage > 0 || time !== '-';
  }

  private isSelectedInsulinType(type: any): boolean {
    if (typeof type === 'string') {
      return true;
    }

    if (type?.selected === false) {
      return false;
    }

    return !!(type?.id || type?.name);
  }

  private resolveInsulinTypeName(type: any): string {
    if (typeof type === 'string') {
      return this.insulinTypeOptions.find((option) => option.id === type)?.name ?? '-';
    }

    if (type?.name) {
      return type.name;
    }

    if (type?.id) {
      return this.insulinTypeOptions.find((option) => option.id === type.id)?.name ?? '-';
    }

    return '-';
  }

  normalizeDisplayTime(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const trimmed = String(value).trim();
    return trimmed && trimmed !== '00:00' && trimmed !== '00:00:00' ? trimmed : '-';
  }

  private normalizeDisplayText(value: string | null | undefined): string {
    const trimmed = value?.trim();
    return trimmed ? trimmed : '-';
  }

  get followUpLabel(): string {
    const pointCount = this.chartSummary.totalPoints ?? 0;
    return `${pointCount || 0} olcum`;
  }

  get selectedWeekLabel(): string {
    if (!this.selectedWeekStart) {
      return '';
    }

    const weekStart = this.selectedWeekStart;
    const weekEnd = this.endOfWeek(weekStart);
    const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
    const sameYear = weekStart.getFullYear() === weekEnd.getFullYear();
    const startLabel = new Intl.DateTimeFormat('tr-TR', {
      day: 'numeric',
      month: 'short',
    }).format(weekStart);
    const endLabel = new Intl.DateTimeFormat('tr-TR', {
      day: 'numeric',
      month: sameMonth && sameYear ? undefined : 'short',
      year: sameYear ? undefined : 'numeric',
    }).format(weekEnd);

    return `${startLabel} - ${endLabel}`;
  }

  get canNavigateToPreviousWeek(): boolean {
    const boundary = this.getMeasurementWeekBoundary('min');
    if (!boundary || !this.selectedWeekStart) {
      return false;
    }

    return this.selectedWeekStart.getTime() > boundary.getTime();
  }

  get canNavigateToNextWeek(): boolean {
    const boundary = this.getMeasurementWeekBoundary('max');
    if (!boundary || !this.selectedWeekStart) {
      return false;
    }

    return this.selectedWeekStart.getTime() < boundary.getTime();
  }

  changeWeek(offset: number): void {
    if (!this.selectedWeekStart || !offset) {
      return;
    }

    this.selectedWeekStart = this.addDays(this.selectedWeekStart, offset * 7);
    this.buildGlucoseReport(this.measurements);
  }

  get targetLowerLimit(): number {
    const lowerLimit =
      this.diabetesCaseInformation?.laboratoryInformation?.targetLowerLimit;
    return lowerLimit && lowerLimit > 20 ? lowerLimit : 70;
  }

  get targetUpperLimit(): number {
    const upperLimit =
      this.diabetesCaseInformation?.laboratoryInformation?.targetUpperLimit;
    return upperLimit && upperLimit > 20 ? upperLimit : 180;
  }

  get hba1cValue(): number | null {
    return this.diabetesCaseInformation?.laboratoryInformation?.hbA1CValue ?? null;
  }

  get averageStatusText(): string {
    const average = this.chartSummary.average;
    if (average === null) {
      return '-';
    }

    if (average < this.targetLowerLimit) {
      return 'Dusuk aralik';
    }

    if (average > this.targetUpperLimit) {
      return 'Yuksek aralik';
    }

    return 'Normal aralik';
  }

  get averageStatusClass(): string {
    const average = this.chartSummary.average;
    if (average === null) {
      return '';
    }

    if (average < this.targetLowerLimit) {
      return 'status-low';
    }

    if (average > this.targetUpperLimit) {
      return 'status-high';
    }

    return 'status-normal';
  }

  get hba1cStatusText(): string {
    const hba1c = this.hba1cValue;
    if (hba1c === null) {
      return '-';
    }

    return hba1c <= 5.6 ? 'Normal' : hba1c <= 6.4 ? 'Riskli' : 'Yuksek';
  }

  get hba1cStatusClass(): string {
    const hba1c = this.hba1cValue;
    if (hba1c === null) {
      return '';
    }

    return hba1c <= 5.6 ? 'status-normal' : hba1c <= 6.4 ? 'status-warn' : 'status-high';
  }

  checkYesOrNo(value: boolean): string {
    return value
      ? this.translateService.instant('yes')
      : this.translateService.instant('no');
  }

  checkMedicationType(value: any): string {
    return value
      ? this.translateService.instant('yes')
      : this.translateService.instant('no');
  }

  checkInsulinAdministrationMethod(value: any): string {
    if (value === InsulinAdministrationMethod.INJECTION) {
      return this.translateService.instant('injection');
    } else if (value === InsulinAdministrationMethod.PUMP) {
      return this.translateService.instant('pump');
    }
    return '-';
  }

  checkTimePeriod(value: number): string {
    if (value === 1) {
      return this.translateService.instant('morning');
    } else if (value === 2) {
      return this.translateService.instant('noon');
    } else if (value === 3) {
      return this.translateService.instant('evening');
    }
    return '-';
  }

  displayDiabetesCaseInformation(): string {
    const chronicConditions = this.diabetesCaseInformation?.chronicCondition;
    if (!chronicConditions) {
      return '-';
    }

    const trueConditions = Object.entries(chronicConditions)
      .filter(([_, value]) => value)
      .map(([key]) =>
        this.translateService.instant('otherImportantChronicDiseases.' + key)
      )
      .join(', ');
    return trueConditions.length > 0 ? trueConditions : '-';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleECGFile(files[0]);
    }
  }

  onECGFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.ecg')) {
        this.isUploading = true;
        if (input.files && input.files.length > 0) {
          this.handleECGFile(input.files[0]);
        }
      } else {
        this.toastService.showToast(
          this.translateService.instant('onlyEcgFilesCanBeUploaded'),
          'warning'
        );
      }
    }
  }

  handleECGFile(file: File) {
    const reader = new FileReader();
    this.uploadFileName = file?.name;
    reader.onload = () => {
      this.uploadedFile = reader.result;

      this.uploadECGFile(this.consultationId, this.uploadedFile);
    };
    reader.readAsDataURL(file);
  }

  onDragDropAreaClick() {
    this.fileInput.nativeElement.click();
  }

  uploadECGFile(id: string, file: any) {
    const formData = new FormData();

    formData.append('PatientRecordCaseId', id || '');
    if (
      this.fileInput?.nativeElement?.files &&
      this.fileInput.nativeElement.files.length > 0
    ) {
      formData.append('ECGFile', this.fileInput.nativeElement.files[0]);
    }

    this.consultationsService.uploadEcgFile(formData).subscribe({
      next: async () => {
        try {
          this.toastService.showToast(
            this.translateService.instant('uploadingFileCompletedSuccessfully'),
            'success'
          );
        } catch (error) {}
      },
    });
  }

  getQrCode(): void {
    this.patientsService.qrCodeForPatientDetail(this.consultationId).subscribe({
      next: (res) => {
        const blob = new Blob([res], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        this.qrCodeDetails = { url: url };
      },
    });
  }

  private buildGlucoseReport(measurements: any[]): void {
    const sortedMeasurements = [...measurements].sort(
      (left, right) =>
        this.getMeasuredTimestamp(left) - this.getMeasuredTimestamp(right)
    );
    const weekStart = this.startOfWeek(
      this.selectedWeekStart ?? this.getLatestMeasurementWeek(sortedMeasurements)
    );
    const weekEnd = this.endOfWeek(weekStart);
    const allValues: number[] = [];
    const chartPoints: GlucoseChartPoint[] = sortedMeasurements
      .filter((measurement) => {
        const measuredDay = this.getMeasuredDate(measurement);
        if (!measuredDay) {
          return false;
        }

        return measuredDay >= this.toDateKey(weekStart) && measuredDay <= this.toDateKey(weekEnd);
      })
      .map((measurement) => {
        const value = this.getNumericValue(measurement);
        const measuredDay = this.getMeasuredDate(measurement);

        if (value === null || !measuredDay) {
          return null;
        }

        allValues.push(value);

        const type = this.getMeasurementKind(measurement);

        return {
          measuredDay,
          measuredTime: measurement?.time ?? '',
          x: this.getDayPositionInWeek(weekStart, measuredDay, measurement?.time),
          fasting: type === 'fasting' ? value : null,
          postprandial: type === 'postprandial' ? value : null,
        };
      })
      .filter((point): point is GlucoseChartPoint => point !== null);
    const fastingValues: GlucoseDatasetPoint[] = chartPoints
      .filter((point) => point.fasting !== null)
      .map((point) => ({
        x: point.x,
        y: point.fasting as number,
        measuredDay: point.measuredDay,
        measuredTime: point.measuredTime,
      }));
    const postprandialValues: GlucoseDatasetPoint[] = chartPoints
      .filter((point) => point.postprandial !== null)
      .map((point) => ({
        x: point.x,
        y: point.postprandial as number,
        measuredDay: point.measuredDay,
        measuredTime: point.measuredTime,
      }));

    this.chartData = {
      datasets: [
        {
          label: 'Aclik',
          type: 'line',
          data: fastingValues,
          borderColor: this.fastingColor,
          backgroundColor: this.fastingColor,
          borderWidth: 3,
          fill: false,
          showLine: true,
          parsing: false,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: this.fastingColor,
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 6,
          tension: 0.38,
          spanGaps: true,
        },
        {
          label: 'Tokluk',
          type: 'line',
          data: postprandialValues,
          borderColor: this.postprandialColor,
          backgroundColor: this.postprandialColor,
          borderWidth: 3,
          fill: false,
          showLine: true,
          parsing: false,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: this.postprandialColor,
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 6,
          tension: 0.38,
          spanGaps: true,
        },
      ],
    };

    const minValue = allValues.length
      ? Math.min(this.targetLowerLimit, ...allValues)
      : this.targetLowerLimit;
    const maxValue = allValues.length
      ? Math.max(this.targetUpperLimit, ...allValues)
      : this.targetUpperLimit;

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'nearest',
        intersect: true,
        axis: 'xy',
      },
      layout: {
        padding: {
          top: 10,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: '#18212f',
          padding: 12,
          displayColors: true,
          mode: 'nearest',
          intersect: true,
          callbacks: {
            title: (tooltipItems) => {
              const firstItem = tooltipItems[0];
              if (!firstItem) {
                return '';
              }

              const point = firstItem.raw as GlucoseDatasetPoint | undefined;
              return point
                ? `${this.formatMeasuredDay(point.measuredDay)} ${point.measuredTime}`.trim()
                : '';
            },
            label: (context) => {
              const value = context.parsed.y;
              if (value === null || value === undefined) {
                return `${context.dataset.label}: -`;
              }
              return `${context.dataset.label}: ${value} mg/dL`;
            },
          },
        },
      } as any,
      scales: {
        x: {
          type: 'linear',
          min: 0,
          max: 7,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12,
              weight: 500,
            },
            stepSize: 1,
            autoSkip: true,
            maxRotation: 0,
            callback: (value: string | number) => {
              const dayIndex = Number(value);
              if (!Number.isInteger(dayIndex) || dayIndex < 0 || dayIndex >= 7) {
                return '';
              }

              return this.formatAxisDay(this.toDateKey(this.addDays(weekStart, dayIndex)));
            },
          },
        },
        y: {
          min: Math.max(0, Math.floor(minValue / 10) * 10 - 20),
          max: Math.ceil(maxValue / 10) * 10 + 20,
          ticks: {
            color: '#6b7280',
            font: {
              size: 12,
            },
          },
          grid: {
            color: 'rgba(148, 163, 184, 0.18)',
          },
          border: {
            display: false,
          },
        },
      },
    };

    this.chartOptions.plugins.targetBand = {
      display: true,
      lower: this.targetLowerLimit,
      upper: this.targetUpperLimit,
      backgroundColor: this.bandColor,
    };

    this.chartSummary = this.calculateSummary(allValues);
  }

  private calculateSummary(values: number[]): GlucoseSummary {
    if (!values.length) {
      return {
        average: null,
        inRangePercentage: 0,
        highPercentage: 0,
        lowPercentage: 0,
        totalPoints: 0,
      };
    }

    const inRange = values.filter(
      (value) => value >= this.targetLowerLimit && value <= this.targetUpperLimit
    ).length;
    const high = values.filter((value) => value > this.targetUpperLimit).length;
    const low = values.filter((value) => value < this.targetLowerLimit).length;
    const average = values.reduce((sum, value) => sum + value, 0) / values.length;

    return {
      average,
      inRangePercentage: this.toPercentage(inRange, values.length),
      highPercentage: this.toPercentage(high, values.length),
      lowPercentage: this.toPercentage(low, values.length),
      totalPoints: values.length,
    };
  }

  private toPercentage(count: number, total: number): number {
    if (!total) {
      return 0;
    }

    return Math.round((count / total) * 100);
  }

  private getNumericValue(measurement: any): number | null {
    const candidate = measurement?.value;
    const parsed = Number(candidate);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getMeasurementKind(measurement: any): MeasurementKind {
    if (measurement?.hungerStatus === 1) {
      return 'fasting';
    }

    if (measurement?.hungerStatus === 2) {
      return 'postprandial';
    }

    return 'unknown';
  }

  private getMeasuredDate(measurement: MeasurementListModel): string | null {
    const measuredDate = measurement?.measuredDate;
    if (typeof measuredDate !== 'string' || !measuredDate.trim()) {
      return null;
    }

    const [datePart] = measuredDate.split('T');
    const normalizedDate = datePart?.trim();

    if (!normalizedDate || !/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
      return null;
    }

    return normalizedDate;
  }

  private getMeasuredTimestamp(measurement: MeasurementListModel): number {
    const measuredDay = this.getMeasuredDate(measurement);
    if (!measuredDay) {
      return 0;
    }

    const measuredTime =
      typeof measurement?.time === 'string' && measurement.time.trim()
        ? measurement.time.trim()
        : '00:00';
    const normalizedTime = /^\d{2}:\d{2}(:\d{2})?$/.test(measuredTime)
      ? measuredTime
      : '00:00';
    const parsed = new Date(`${measuredDay}T${normalizedTime}`).getTime();

    if (!Number.isNaN(parsed)) {
      return parsed;
    }

    return this.getDateKeyTimestamp(measuredDay);
  }

  private getDateKeyTimestamp(dateKey: string): number {
    const parsed = new Date(`${dateKey}T00:00:00`).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private initializeSelectedWeek(measurements: MeasurementListModel[]): void {
    const latestWeek = this.getLatestMeasurementWeek(measurements);
    this.selectedWeekStart = this.startOfWeek(latestWeek);
  }

  private getLatestMeasurementWeek(
    measurements: MeasurementListModel[]
  ): Date {
    const timestamps = measurements
      .map((measurement) => this.getMeasuredTimestamp(measurement))
      .filter((timestamp) => timestamp > 0);

    if (!timestamps.length) {
      return this.startOfWeek(new Date());
    }

    return this.startOfWeek(new Date(Math.max(...timestamps)));
  }

  private getMeasurementWeekBoundary(type: 'min' | 'max'): Date | null {
    const timestamps = this.measurements
      .map((measurement) => this.getMeasuredTimestamp(measurement))
      .filter((timestamp) => timestamp > 0);

    if (!timestamps.length) {
      return null;
    }

    const boundaryTimestamp =
      type === 'min' ? Math.min(...timestamps) : Math.max(...timestamps);
    return this.startOfWeek(new Date(boundaryTimestamp));
  }

  private startOfWeek(date: Date): Date {
    const start = new Date(date);
    const day = start.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    start.setDate(start.getDate() + diff);
    start.setHours(0, 0, 0, 0);
    return start;
  }

  private endOfWeek(date: Date): Date {
    const end = this.addDays(date, 6);
    end.setHours(0, 0, 0, 0);
    return end;
  }

  private addDays(date: Date, days: number): Date {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    next.setHours(0, 0, 0, 0);
    return next;
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getDayPositionInWeek(
    weekStart: Date,
    measuredDay: string,
    measuredTime?: string
  ): number {
    const day = this.getDateKeyTimestamp(measuredDay);

    if (!Number.isFinite(day)) {
      return 0;
    }

    const diffDays = Math.floor(
      (day - this.getDateKeyTimestamp(this.toDateKey(weekStart))) / 86400000
    );
    const fraction = this.getTimeFraction(measuredTime);
    return Math.min(diffDays + fraction, diffDays + 0.9993);
  }

  private getTimeFraction(measuredTime?: string): number {
    if (typeof measuredTime !== 'string') {
      return 0;
    }

    const match = measuredTime.trim().match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) {
      return 0;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3] ?? '0');

    return (hours * 3600 + minutes * 60 + seconds) / 86400;
  }

  private formatAxisDay(measuredDay: string): string {
    const parsed = new Date(`${measuredDay}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return measuredDay;
    }

    return new Intl.DateTimeFormat('tr-TR', {
      day: 'numeric',
      month: 'short',
    }).format(parsed);
  }

  private formatMeasuredDay(measuredDay: string): string {
    const parsed = new Date(`${measuredDay}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return measuredDay;
    }

    return parsed.toLocaleDateString('tr-TR');
  }
}
